import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { landingTranslations, LandingLang } from "@/i18n/landing";

const STORAGE_KEY = "maestro-landing-lang";

type DeepStringify<T> = {
  [K in keyof T]: T[K] extends object ? DeepStringify<T[K]> : string;
};

export type LandingTranslation = DeepStringify<typeof landingTranslations.it>;

interface LandingLanguageContextType {
  lang: LandingLang;
  setLang: (lang: LandingLang) => void;
  t: LandingTranslation;
}

const LandingLanguageContext = createContext<LandingLanguageContextType | null>(null);

function getInitialLang(): LandingLang {
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "it") {
      return stored;
    }
    // Default to Italian for Italian browsers, English otherwise
    const browserLang = navigator.language.toLowerCase();
    return browserLang.startsWith("it") ? "it" : "en";
  }
  return "it";
}

export function LandingLanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<LandingLang>(getInitialLang);

  const setLang = (newLang: LandingLang) => {
    console.log('LandingLanguage: Switching from', lang, 'to', newLang);
    setLangState(newLang);
    localStorage.setItem(STORAGE_KEY, newLang);
  };

  // Sync with localStorage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === "en" || e.newValue === "it")) {
        setLangState(e.newValue);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const t = landingTranslations[lang] as LandingTranslation;

  const value: LandingLanguageContextType = { lang, setLang, t };

  return (
    <LandingLanguageContext.Provider value={value}>
      {children}
    </LandingLanguageContext.Provider>
  );
}

// Default fallback for when hook is used outside provider (graceful degradation)
const defaultContext: LandingLanguageContextType = {
  lang: "it",
  setLang: () => console.warn("useLandingLanguage: setLang called outside provider"),
  t: landingTranslations.it as LandingTranslation,
};

export function useLandingLanguage(): LandingLanguageContextType {
  const context = useContext(LandingLanguageContext);
  
  // Return default context instead of throwing - allows graceful HMR and edge cases
  if (!context) {
    return defaultContext;
  }
  
  return context;
}
